
/**
 * ThreeSceneManager.jsx
 *
 * Manages the Three.js scene, renderer, camera, controls, helpers, and VR/session logic for the embedding visualization.
 * Exposes refs for scene, renderer, camera, etc. via forwardRef, and provides a context for child components.
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */
// src/components/ThreeSceneManager.jsx

import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { useThreeScene } from '../hooks/useThreeScene';

/**
 * ThreeSceneManager
 *
 * React component that manages the Three.js scene, renderer, camera, controls, helpers, and VR/session logic.
 * Exposes refs for scene, renderer, camera, etc. via forwardRef.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.canvasRef - Ref to the canvas element
 * @param {function} [props.onSceneReady] - Callback({ scene }) when scene is ready
 * @param {function} [props.onEnterVR] - Callback to trigger VR session
 * @param {boolean} [props.showHelpers] - Whether to show axes/grid helpers
 * @param {React.ReactNode} props.children - Child components (e.g., EmbeddingsGroup)
 * @returns {JSX.Element}
 */
const ThreeSceneManager = forwardRef(function ThreeSceneManager({ canvasRef, onSceneReady, onEnterVR, showHelpers = true, children }, ref) {
  // Toggle visibility of helpers (axes/grid) when showHelpers changes
  useEffect(() => {
    if (axesHelperRef.current) axesHelperRef.current.visible = showHelpers;
    if (polarGridHelperRef.current) polarGridHelperRef.current.visible = showHelpers;
  }, [showHelpers]);
  // Refs for renderer, scene, and group transforms (for VR/web)
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const webGroupTransform = useRef({ position: null, scale: null });
  const vrGroupTransform = useRef({ position: new THREE.Vector3(0, 1.6, 0), scale: new THREE.Vector3(2, 2, 2) });

  // Get controls, helpers, camera, etc. from custom hook
  const { axesHelperRef, polarGridHelperRef, controlsRef, rigRef, cameraRef } = useThreeScene(canvasRef, { onReady: handleSceneReady });

  // Scene setup callback (called by useThreeScene when scene is ready)
  function handleSceneReady({ scene }) {
    sceneRef.current = scene;
    if (onSceneReady) onSceneReady({ scene });
    // Create renderer if not already present
    if (!rendererRef.current && canvasRef.current) {
      const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.xr.enabled = true;
      rendererRef.current = renderer;
    }
  }

  // VR session handler (starts immersive-ar session and applies VR transforms)
  const handleEnterVRInternal = () => {
    if (!rendererRef.current) {
      alert('Renderer not ready. Try again after the scene loads.');
      return;
    }
    if (!navigator.xr) {
      alert('WebXR not supported in this browser.');
      return;
    }
    rendererRef.current.xr.enabled = true;
    rendererRef.current.xr.setSession(null);
    navigator.xr.requestSession('immersive-ar', { optionalFeatures: ['local-floor', 'bounded-floor'] })
      .then(session => {
        rendererRef.current.xr.setSession(session);
        // Save/apply VR transform
        const group = sceneRef.current;
        if (group) {
          // Save web transform if not already saved
          if (!webGroupTransform.current.position) {
            webGroupTransform.current.position = group.position.clone();
            webGroupTransform.current.scale = group.scale.clone();
          }
          // Apply VR transform
          group.scale.copy(vrGroupTransform.current.scale);
          group.position.copy(vrGroupTransform.current.position);
        }
        // Restore web transform when VR session ends
        session.addEventListener('end', () => {
          const group = sceneRef.current;
          if (group && webGroupTransform.current.position) {
            group.scale.copy(webGroupTransform.current.scale);
            group.position.copy(webGroupTransform.current.position);
          }
        });
        // Animation/render loop for VR
        rendererRef.current.setAnimationLoop(() => {
          if (sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
        });
      })
      .catch(err => {
        let userMessage = 'Oops, something went wrong starting the VR session.';
        if (err && err.message && err.message.includes('The specified session configuration is not supported')) {
          userMessage += '\n\nThis may be because your browser or device does not support WebXR immersive VR, or no VR headset is connected.\n\n';
          userMessage += '• Make sure you are using a compatible browser (latest Chrome or Edge, not Safari/Firefox).\n';
          userMessage += '• Ensure your VR headset is connected and powered on.\n';
          userMessage += '• Check browser settings or flags for WebXR support.';
        } else if (err && err.message) {
          userMessage += '\n' + err.message;
        }
        alert(userMessage);
      });
  };


  // Method to update axes helper startColor and endColor only
  const updateAxesHelper_color = ({ startColor, endColor }) => {
    console.log("[ThreeSceneManager] updateAxesHelper_color called with:", { startColor, endColor });
    if (!axesHelperRef.current) {
      console.warn("[ThreeSceneManager] updateAxesHelper_color called but axesHelperRef is not set.");
      return;
    }
    if (!sceneRef.current || !axesHelperRef.current) return;
    // Only update colors, preserve all other properties
    if (startColor) axesHelperRef.current.setStartColor(startColor);
    if (endColor) axesHelperRef.current.setEndColor(endColor);
  };

  // Expose refs, VR handler, and updateAxesHelper to parent via forwardRef
  useImperativeHandle(ref, () => ({
    rendererRef,
    sceneRef,
    controlsRef,
    rigRef,
    cameraRef,
    axesHelperRef,
    polarGridHelperRef,
    handleEnterVR: handleEnterVRInternal,
    updateAxesHelper_color,
  }), []);

  // Optionally, call onEnterVR from parent (if provided)
  useEffect(() => {
    if (onEnterVR) {
      onEnterVR(() => handleEnterVRInternal());
    }
  }, [onEnterVR]);

  // Render children (e.g., EmbeddingsGroup) with refs/context
  return (
    <>{children}</>
  );
});

export default ThreeSceneManager;
